import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Clock, Gift, LogOut, QrCode, ShieldX, XCircle } from 'lucide-react';
import { EXIT_STATE, type ExitState } from '@garlic/shared';
import { api, errorMessage, post } from '../../lib/api';
import { formatBRL, formatDateTime } from '../../lib/format';
import { Alert, Badge, Button, Card, CardHeader, Input, PageHeader, cx } from '../../ui/primitives';
import { useToast } from '../../ui/feedback';
import { QrScanner } from '../../ui/QrScanner';
import type { TenantRestaurant } from '../TenantApp';

type Check = {
  result: ExitState; reason: string | null; receiptId: string | null; tableLabel?: string; sessionNumber?: number;
  consumedCents?: number; paidCents?: number; dueCents?: number; processingCents?: number; settledAt?: string; usedAt?: string | null; usedBy?: string | null; sessionClosed?: boolean;
  gift: { giftEnabled: boolean; giftNote: string | null };
};
type HistoryRow = { id: string; result: ExitState; reason: string | null; confirmed: boolean; giftDelivered: boolean; dueCents: number | null; createdAt: string; by: string | null; tableLabel: string | null; sessionNumber: number | null };

const STYLE: Record<ExitState, { box: string; icon: typeof CheckCircle2 }> = {
  PAID: { box: 'border-sprout-500 bg-sprout-100 text-sprout-600', icon: CheckCircle2 },
  PENDING: { box: 'border-danger-600 bg-danger-50 text-danger-600', icon: XCircle },
  PROCESSING: { box: 'border-warn-600 bg-warn-50 text-warn-600', icon: Clock },
  INVALID: { box: 'border-danger-600 bg-danger-50 text-danger-600', icon: ShieldX },
  ALREADY_VALIDATED: { box: 'border-warn-600 bg-warn-50 text-warn-600', icon: AlertTriangle },
};

/** Tela do balconista: lê/digita o código, mostra o estado REAL da conta e registra a saída (uma vez por comprovante). */
export function StaffExitDesk({ restaurant }: { restaurant: TenantRestaurant }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [code, setCode] = useState('');
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [check, setCheck] = useState<Check | null>(null);
  const [gift, setGift] = useState(true);
  const input = useRef<HTMLInputElement>(null);
  const history = useQuery({ queryKey: ['staff-exit-history'], queryFn: () => api<HistoryRow[]>('/api/staff/exit/validations') });

  const verify = useCallback(async (raw: string) => {
    const value = raw.trim();
    if (!value) return;
    setBusy(true);
    try {
      const r = await post<Check>('/api/staff/exit/check', { code: value });
      setCheck(r);
      setGift(true);
      void qc.invalidateQueries({ queryKey: ['staff-exit-history'] });
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }, [qc, toast]);

  useEffect(() => input.current?.focus(), []);

  const confirm = async () => {
    if (!check?.receiptId) return;
    setBusy(true);
    try {
      await post(`/api/staff/exit/${check.receiptId}/confirm`, { giftDelivered: gift });
      toast.success(`Saída registrada — mesa ${check.tableLabel}`);
      setCheck(null);
      setCode('');
      void qc.invalidateQueries({ queryKey: ['staff-exit-history'] });
      input.current?.focus();
    } catch (e) {
      toast.error(errorMessage(e));
      await verify(code);
    } finally {
      setBusy(false);
    }
  };

  const onScan = useCallback((text: string) => {
    setScanning(false);
    setCode(text);
    void verify(text);
  }, [verify]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void verify(code);
  };

  const state = check ? EXIT_STATE[check.result] : null;
  const S = check ? STYLE[check.result] : null;

  return (
    <>
      <PageHeader title="Saída" description="Confira o comprovante do cliente. O resultado vem da conta real no servidor, não da tela do celular." />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_22rem]">
        <div className="min-w-0 space-y-4">
          <Card className="p-5">
            <form onSubmit={submit} className="flex flex-col gap-3 sm:flex-row">
              <Input
                ref={input}
                aria-label="Código de validação"
                placeholder="Código do comprovante (ex.: ABCDE-FGH23)"
                className="h-14 w-full min-w-0 flex-1 text-center font-mono text-2xl uppercase tracking-widest placeholder:text-base placeholder:tracking-normal"
                value={code}
                autoComplete="off"
                onChange={(e) => setCode(e.target.value.toUpperCase())}
              />
              <Button type="submit" size="lg" className="h-14" loading={busy}>Verificar</Button>
              <Button size="lg" variant="secondary" className="h-14" icon={<QrCode className="size-5" />} onClick={() => setScanning(true)}>Escanear</Button>
            </form>
            {scanning && <div className="mx-auto mt-4 max-w-sm"><QrScanner onResult={onScan} onClose={() => setScanning(false)} /></div>}
          </Card>

          {check && state && S && (
            <div role="status" aria-live="assertive" className={cx('rounded-3xl border-4 p-6', S.box)}>
              <div className="flex items-center gap-3">
                <S.icon className="size-10 shrink-0" />
                <div>
                  <p className="font-display text-3xl font-bold tracking-tight">{state.title}</p>
                  <p className="font-semibold">{state.subtitle}{check.reason && check.result !== 'PAID' ? ` — ${check.reason}` : ''}</p>
                </div>
              </div>
              {check.tableLabel && (
                <dl className="mt-5 grid grid-cols-2 gap-3 text-sm text-ink sm:grid-cols-4">
                  <div><dt className="text-ink-3">Mesa</dt><dd className="font-display text-2xl font-semibold">{check.tableLabel}</dd></div>
                  <div><dt className="text-ink-3">Comanda</dt><dd className="font-semibold">nº {check.sessionNumber}{check.sessionClosed ? ' (encerrada)' : ''}</dd></div>
                  {check.consumedCents !== undefined && <div><dt className="text-ink-3">Consumido</dt><dd className="font-semibold tabular-nums">{formatBRL(check.consumedCents)}</dd></div>}
                  {check.paidCents !== undefined && <div><dt className="text-ink-3">Pago</dt><dd className="font-semibold tabular-nums">{formatBRL(check.paidCents)}</dd></div>}
                </dl>
              )}
              {check.result === 'PENDING' && <p className="mt-4 text-2xl font-bold text-ink">Falta pagar: {formatBRL(check.dueCents ?? 0)}</p>}
              {check.result === 'PROCESSING' && <p className="mt-4 text-ink">{formatBRL(check.processingCents ?? 0)} aguardando confirmação do Mercado Pago. Peça para o cliente aguardar alguns instantes e verifique de novo.</p>}
              {check.result === 'ALREADY_VALIDATED' && check.usedAt && <p className="mt-4 text-ink">Saída registrada em {formatDateTime(check.usedAt, restaurant.timezone)}{check.usedBy ? ` por ${check.usedBy}` : ''}.</p>}
              {check.result === 'INVALID' && <p className="mt-4 text-ink">Confira com o cliente ou consulte a conta da mesa em “Contas e caixa”.</p>}
              {check.result === 'PAID' && (
                <div className="mt-5 space-y-3">
                  {check.gift.giftEnabled && (
                    <label className="flex items-center gap-3 rounded-xl bg-white/80 px-4 py-3 text-ink">
                      <input type="checkbox" className="size-5 accent-sprout-600" checked={gift} onChange={(e) => setGift(e.target.checked)} />
                      <Gift className="size-5 text-sprout-600" />
                      <span>Brinde entregue{check.gift.giftNote ? `: ${check.gift.giftNote}` : ''}</span>
                    </label>
                  )}
                  <Button size="lg" className="h-14 w-full bg-sprout-600 text-lg hover:bg-sprout-600/90" loading={busy} icon={<LogOut className="size-5" />} onClick={confirm}>Registrar saída</Button>
                </div>
              )}
              {check.result !== 'PAID' && (
                <Button variant="secondary" className="mt-4" onClick={() => void verify(code)} loading={busy}>Verificar de novo</Button>
              )}
            </div>
          )}
        </div>

        <Card className="self-start">
          <CardHeader title="Últimas verificações" />
          {history.data && history.data.length === 0 && <p className="px-5 py-4 text-sm text-ink-3">Nenhuma ainda.</p>}
          <ul className="divide-y divide-line text-sm">
            {history.data?.map((h) => (
              <li key={h.id} className="flex items-start justify-between gap-2 px-5 py-2.5">
                <span className="min-w-0">
                  <span className="font-medium">{h.tableLabel ? `Mesa ${h.tableLabel}` : 'Código inválido'}</span>
                  <span className="block truncate text-xs text-ink-3">{formatDateTime(h.createdAt, restaurant.timezone)}{h.by ? ` · ${h.by}` : ''}{h.giftDelivered ? ' · brinde' : ''}</span>
                </span>
                <Badge tone={h.confirmed ? 'success' : h.result === 'PAID' ? 'brand' : h.result === 'INVALID' || h.result === 'PENDING' ? 'danger' : 'warning'}>
                  {h.confirmed ? 'Saída registrada' : EXIT_STATE[h.result].title.toLowerCase()}
                </Badge>
              </li>
            ))}
          </ul>
        </Card>
      </div>
      {!history.isPending && !history.data && <Alert tone="danger">Não foi possível carregar o histórico.</Alert>}
    </>
  );
}
